import { buildPreprocessVariants } from './imagePreprocess'

const TESSERACT = {
  workerPath: 'https://unpkg.com/tesseract.js@5.0.5/dist/worker.min.js',
  corePath: 'https://unpkg.com/tesseract.js-core@5.0.0/tesseract-core.wasm.js',
  langPath: 'https://tessdata.projectnaptha.com/4.0.0',
  lang: 'por',
}

type TesseractWorker = {
  setParameters: (params: Record<string, string>) => Promise<unknown>
  recognize: (
    image: File | Blob
  ) => Promise<{ data?: { text?: string; confidence?: number } }>
}

export type OcrProgressHandler = (message: string, progress?: number) => void

export type OcrVariantResult = {
  label: string
  confidence: number
}

export type OcrResult = {
  text: string
  confidence: number | null
  char_count: number
  variant: string | null
  variants_tried: OcrVariantResult[] | string[]
  prepared_image?: File
}

let workerPromise: Promise<TesseractWorker> | null = null
let progressHandler: OcrProgressHandler | null = null

async function ensureWorker(): Promise<TesseractWorker> {
  if (!workerPromise) {
    workerPromise = (async () => {
      const module = (await import(
        /* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/tesseract.js@5.0.5/+esm'
      )) as {
        createWorker: (
          lang: string,
          oem: number,
          opts: Record<string, unknown>
        ) => Promise<TesseractWorker>
      }

      const worker = await module.createWorker(TESSERACT.lang, 1, {
        workerPath: TESSERACT.workerPath,
        corePath: TESSERACT.corePath,
        langPath: TESSERACT.langPath,
        logger: (message: { status?: string; progress?: number }) => {
          if (
            progressHandler &&
            message.status === 'recognizing text' &&
            typeof message.progress === 'number'
          ) {
            progressHandler(
              'Extraindo texto do cupom...',
              25 + Math.round(message.progress * 70)
            )
          }
        },
      })

      await worker.setParameters({
        tessedit_pageseg_mode: '6',
        user_defined_dpi: '300',
        preserve_interword_spaces: '1',
      })

      return worker
    })().catch((error: unknown) => {
      // A failed CDN download must not poison every subsequent attempt in this tab.
      workerPromise = null
      throw error
    })
  }
  return workerPromise
}

function mergeOcrTexts(texts: string[]): string {
  const lineSet = new Set<string>()
  const ordered: string[] = []

  for (const text of texts) {
    for (const rawLine of text.split(/\r?\n/)) {
      const line = rawLine.trim()
      if (!line || line.length < 3) continue
      const key = line.toLowerCase()
      if (!lineSet.has(key)) {
        lineSet.add(key)
        ordered.push(line)
      }
    }
  }

  return ordered.join('\n')
}

export async function runOcrOnFile(
  file: File | Blob,
  onProgress?: OcrProgressHandler
): Promise<OcrResult> {
  progressHandler = onProgress ?? null

  try {
    onProgress?.('Pré-processando imagem (crop + upscale)...', 5)
    const variants = await buildPreprocessVariants(file)

    onProgress?.('Carregando motor OCR (primeira vez pode demorar)...', 12)
    const worker = await ensureWorker()

    const results: Array<{ label: string; text: string; confidence: number }> = []

    for (let index = 0; index < variants.length; index += 1) {
      const variant = variants[index]
      const baseProgress = 15 + Math.round((index / variants.length) * 75)
      onProgress?.(`OCR (${variant.label})...`, baseProgress)

      const { data } = await worker.recognize(variant.file)
      const text = (data?.text || '').trim()
      const confidence = typeof data?.confidence === 'number' ? data.confidence : 0

      if (text) {
        results.push({ label: variant.label, text, confidence })
      }
    }

    if (results.length === 0) {
      return {
        text: '',
        confidence: null,
        char_count: 0,
        variant: null,
        variants_tried: variants.map((v) => v.label),
      }
    }

    results.sort((a, b) => b.confidence - a.confidence || b.text.length - a.text.length)
    const best = results[0]
    const mergedText = mergeOcrTexts(results.map((r) => r.text))
    const text = mergedText.length >= best.text.length * 0.85 ? mergedText : best.text
    const confidence = Math.max(0, Math.min(1, best.confidence / 100))

    onProgress?.('OCR concluído.', 100)

    return {
      text,
      confidence,
      char_count: text.length,
      variant: best.label,
      variants_tried: results.map((r) => ({ label: r.label, confidence: r.confidence })),
      prepared_image: variants[0].file,
    }
  } finally {
    progressHandler = null
  }
}
